/* eslint-disable @typescript-eslint/no-explicit-any */
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { createErrorHandler } from '@sentry/angular-ivy';

import { EffectsModule } from '@ngrx/effects';
import { ActionReducer, INIT, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { DialogService } from 'primeng/dynamicdialog';
import { environment } from 'src/environments/environment';
import { UserService } from './core/services/user.service';

import '@angular/common/locales/global/nl';
import '@angular/common/locales/global/sv';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';
import { AccountingListComponent } from './accounting/accounting.list.component';
import { ActivitiesListComponent } from './activity/activities.list.component';
// import { AdvisoryToolModule } from './advisory-tool/advisory-tool.module';
import { AppComponent } from './app.component';
import { routing } from './app.routing';
import { ArchiveClientDialogComponent } from './client/archive-client-dialog/archive-client-dialog.component';
import { ClientListComponent } from './client/client.list.component';
import { CompareCloudCompaniesDialogComponent } from './client/compare-cloud-companies/compare-cloud-companies-dialog.component';
import { ExportCompaniesToCloudComponent } from './client/compare-cloud-companies/components/export-companies-to-cloud.component';
import { ImportMissingCompaniesToBystComponent } from './client/compare-cloud-companies/components/import-missing-companies-to-byst.component';
import { SyncCompaniesBetweenBystCloudComponent } from './client/compare-cloud-companies/components/sync-companies-between-byst-cloud.component';
import { NewClientGuideDialogComponent } from './client/new-client-guide-dialog/new-client-guide-dialog.component';
import { ClosingListComponent } from './closing/closing.list.component';
import { AnalyticService } from './core/entity/analytics/analytic-tracking';
import { DynamicLocaleId } from './core/i18n/DynamicLocaleId';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { MatomoAnalyticService } from './core/services/analytics/matomo-analytic.service';
import { NoOpAnalyticService } from './core/services/analytics/no-op-analytic.service';
import { MessagingService } from './core/services/messaging.service';
import { PeriodService } from './core/services/period.service';
import { ActivityEffects } from './core/state/activities/activities.effect';
import { activitiesReducer } from './core/state/activities/activities.reducers';
import { AppState } from './core/state/appState';
import { AssignmentEffects } from './core/state/assignments/assignments.effect';
import { assignmentsReducer } from './core/state/assignments/assignments.reducers';
import { AuthActions } from './core/state/auth/auth.actions';
import { AuthEffects } from './core/state/auth/auth.effects';
import { authReducer } from './core/state/auth/auth.reducers';
import { ClientEffects } from './core/state/clients/clients.effect';
import { clientsReducer } from './core/state/clients/clients.reducers';
import { ClientTasksEffects } from './core/state/clientTasks/clientTasks.effects';
import { clientTasksFeature } from './core/state/clientTasks/clientTasks.reducer';
import { ClientTypeEffects } from './core/state/clientTypes/clientTypes.effect';
import { clientTypesReducer } from './core/state/clientTypes/clientTypes.reducers';
import { CollaborationPackageEffects } from './core/state/collaboration-packages/collaboration-packages.effects';
import { collaborationPackageItemReducer } from './core/state/collaboration-packages/collaboration-packages.reducers';
import { CompositeEffects } from './core/state/composite/composite.effects';
import { CoreEffects } from './core/state/core/core.effects';
import { CreditInformationEffects } from './core/state/credit-information/credit-information.effects';
import { creditInformationFeature } from './core/state/credit-information/credit-information.reducer';
import { ExportWizardEffects } from './core/state/export-wizard/export-wizard.effects';
import { exportWizardFeature } from './core/state/export-wizard/export-wizard.feature';
import { featuresFeature } from './core/state/feature/feature.reducer';
import { FileVaultEffects } from './core/state/filevault/filevault.effect';
import { fileVaultReducer } from './core/state/filevault/filevault.reducers';
import { FirmCollaboratorEffects } from './core/state/firm-collaborators/firm-collaborators.effect';
import { firmCollaboratorFeature } from './core/state/firm-collaborators/firm-collaborators.reducer';
import { FirmEffects } from './core/state/firm/firm.effects';
import { firmReducer } from './core/state/firm/firm.reducers';
import { I18nEffects } from './core/state/i18n/i18n.effects';
import { i18nFeature } from './core/state/i18n/i18n.reducers';
import { listsReducer } from './core/state/list/list.reducers';
import { MessagingEffects } from './core/state/messaging/messaging.effects';
import { navigationReducer } from './core/state/navigation/navigation.reducers';
import { PartnerProgramEffects } from './core/state/partnerprogram/partnerprogram.effects';
import { partnerProgramFeature } from './core/state/partnerprogram/partnerprogram.reducer';
import { RuleEffects } from './core/state/rule/rule.effects';
import { ruleFeature } from './core/state/rule/rule.reducer';
import { TaskGroupEffects } from './core/state/task-group/task-group.effects';
import { TaskGroupsEffects } from './core/state/task-groups/task-groups.effects';
import { taskGroupsFeature } from './core/state/task-groups/task-groups.reducer';
import { TaskTemplateEffects } from './core/state/task-templates/task-templates.effects';
import { taskTemplateFeature } from './core/state/task-templates/task-templates.reducers';
import { TaskEffects } from './core/state/tasks/task.effects';
import { TeamEffects } from './core/state/team/team.effects';
import { teamReducer } from './core/state/team/team.reducers';
import { TeamsEffects } from './core/state/teams/teams.effects';
import { teamsReducer } from './core/state/teams/teams.reducers';
import { ToastEffects } from './core/state/toast/toast.effects';
import { TodoStatesEffects } from './core/state/todo-states/todo-states.effects';
import { todoStatesFeature } from './core/state/todo-states/todo-states.reducers';
import { TodoEffects } from './core/state/todo/todo.effect';
import { TodosEffects } from './core/state/todos/todos.effects';
import { UserEffects } from './core/state/users/users.effects';
import { usersReducer } from './core/state/users/users.reducers';
import { BlFrontendNavMenuComponent } from './core/wrappers/bl-frontend-nav-menu.component';
import { FirmSettingsModule } from './firm-settings/firm-settings.module';
import { IncomeStatementListComponent } from './incomestatement/incomestatement.list.component';
import { IncomeTaxDeclarationListComponent } from './incometaxdeclaration/incometaxdeclaration.list.component';
import { BlaAuthComponent } from './login/bla-auth.component';
import { LoginComponent } from './login/login.component';
import { SilentAuthComponent } from './login/silent-auth.component';
import { NotificationsComponent } from './notifications/notifications.component';
import { PartnerProgramModule } from './partnerprogram/partnerprogram.module';
import { SalaryListComponent } from './salary/salary.list.component';
import { BystSharedModule } from './shared/byst-shared.module';
import { NavbarComponent } from './shared/components/navbar/navbar.component';
import { TodoStatusSelectorComponent } from './shared/components/todo-status-selector/todo-status-selector.component';
import { HideIfCountriesDirective } from './shared/directives/hide-if-countries.directive';
import { DEFAULT_LANGUAGE } from './shared/misc/constants';
import { MainAppComponent } from './start/main-app.component';
import { StartIframeComponent } from './start/start-iframe.component';
import { TaxDeclarationListComponent } from './taxdeclaration/taxdeclaration.list.component';
import { TodoListComponent } from './todo/todo.list.component';
import { TranslocoRootModule } from './transloco-root.module';
import { AmlButtonComponent } from './client/aml-button/aml-button.component';
import { UserSelectorComponent } from './shared/components/user-selector/user-selector.component';
import { AnalyticsEffects } from './core/state/analytics/analytics.effect';
import { SnippetDirective } from './core/wrappers/snippet-directives/snippet.directive';

export function errorHandlerFactory() {
  if (environment.production) {
    // return Sentry.createErrorHandler({ showDialog: false, logErrors: true });
    return createErrorHandler({ showDialog: false, logErrors: true }); // new SentryErrorHandler();
  }
  return new ErrorHandler();
}

function getBrowserLang() {
  const validLangs = ['sv', 'nl', 'en']; // Supported languages
  const browserLang = navigator.language.split('-')[0]?.toLowerCase(); // Get the language code (e.g., 'en', 'sv', 'nl')
  return validLangs.includes(browserLang) ? browserLang : DEFAULT_LANGUAGE; // Default to Swedish if not supported
}

function getUserLang(transloco: TranslocoService) {
  return function ret() {
    // const lang = new UserSettingsStorage().loadSetting(UserSettingsStorage.LANGUAGE, DEFAULT_LANGUAGE);
    const lang = getBrowserLang();
    transloco.setActiveLang(lang);
    return firstValueFrom(transloco.load(lang));
  };
}

type RootReducer = { [P in keyof AppState]: ActionReducer<any, any> };
const reducers: RootReducer = {
  i18n: i18nFeature.reducer,
  activities: activitiesReducer,
  assignments: assignmentsReducer,
  auth: authReducer,
  firm: firmReducer,
  clients: clientsReducer,
  users: usersReducer,
  list: listsReducer,
  fileVault: fileVaultReducer,
  navigation: navigationReducer,
  clientTypes: clientTypesReducer,
  taskTemplates: taskTemplateFeature.reducer,
  rules: ruleFeature.reducer,
  exportWizard: exportWizardFeature.reducer,
  taskGroups: taskGroupsFeature.reducer,
  teams: teamsReducer,
  team: teamReducer,
  clientTasks: clientTasksFeature.reducer,
  features: featuresFeature.reducer,
  creditInformation: creditInformationFeature.reducer,
  collaborationPackageItem: collaborationPackageItemReducer,
  partnerProgram: partnerProgramFeature.reducer,
  firmCollaborators: firmCollaboratorFeature.reducer,
  todoStates: todoStatesFeature.reducer,
};

// clear state when user logout
const metaReduser =
  (reducer: ActionReducer<any>): ActionReducer<any> =>
  (state, action) => {
    if (action.type === AuthActions.resetAuthStateAndStorage.type) {
      // eslint-disable-next-line no-undefined
      return reducer(undefined, { type: INIT });
    }
    return reducer(state, action);
  };

const ANALYTIC_SERVICE_PROVIDER = {
  provide: AnalyticService,
  useClass: environment.matomo.active ? MatomoAnalyticService : NoOpAnalyticService,
};

@NgModule({
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    TranslocoRootModule,
    routing,
    BystSharedModule,
    // AdvisoryToolModule,
    FirmSettingsModule,
    PartnerProgramModule,
    NewClientGuideDialogComponent,
    NavbarComponent,
    TodoStatusSelectorComponent,
    BlFrontendNavMenuComponent,
    AmlButtonComponent,
    UserSelectorComponent,
    HideIfCountriesDirective,
    SnippetDirective,
    StoreModule.forRoot(reducers, { metaReducers: [metaReduser] }),
    StoreDevtoolsModule.instrument({ name: 'Workflow', maxAge: 500, connectInZone: true }),
    EffectsModule.forRoot([
      AnalyticsEffects,
      AuthEffects,
      CoreEffects,
      ToastEffects,
      I18nEffects,
      UserEffects,
      AssignmentEffects,
      ClientEffects,
      TodoEffects,
      FirmEffects,
      ActivityEffects,
      FileVaultEffects,
      ClientTypeEffects,
      TeamsEffects,
      TeamEffects,
      TodosEffects,
      TaskTemplateEffects,
      RuleEffects,
      CompositeEffects,
      TaskEffects,
      ExportWizardEffects,
      TaskGroupsEffects,
      TaskGroupEffects,
      ClientTasksEffects,
      MessagingEffects,
      CreditInformationEffects,
      CollaborationPackageEffects,
      PartnerProgramEffects,
      FirmCollaboratorEffects,
      TodoStatesEffects,
    ]),
  ],
  declarations: [
    AppComponent,
    MainAppComponent,
    LoginComponent,
    SilentAuthComponent,
    BlaAuthComponent,
    CompareCloudCompaniesDialogComponent,
    ImportMissingCompaniesToBystComponent,
    ExportCompaniesToCloudComponent,
    SyncCompaniesBetweenBystCloudComponent,
    ActivitiesListComponent,
    AccountingListComponent,
    IncomeStatementListComponent,
    ClosingListComponent,
    SalaryListComponent,
    IncomeTaxDeclarationListComponent,
    TaxDeclarationListComponent,
    TodoListComponent,
    StartIframeComponent,
    NotificationsComponent,
    ArchiveClientDialogComponent,
    ClientListComponent,
  ],
  providers: [
    UserService,
    PeriodService,
    { provide: ErrorHandler, useFactory: errorHandlerFactory },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: LOCALE_ID, useClass: DynamicLocaleId, deps: [TranslocoService] },
    { provide: APP_INITIALIZER, multi: true, deps: [TranslocoService], useFactory: getUserLang },
    DialogService,
    MessagingService,
    ANALYTIC_SERVICE_PROVIDER,
  ],
  bootstrap: [AppComponent],
})
// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class AppModule {}
